import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@dcp/shared/types/database'
import type { TenantTables } from '@dcp/shared/types/tenant-database'
import type { DbResult } from '@/lib/db-tenant'
import { pageQueryError, totalsRowOf } from '@/lib/pagination'

/** The columns the "View transactions" dialog shows for a credit card. */
export type CreditCardTransaction = Pick<
  TenantTables['transactions']['Row'],
  'id' | 'transaction_date' | 'amount' | 'description' | 'vendor' | 'pending'
>

/**
 * One page of one card's transactions, optionally bounded by inclusive
 * `YYYY-MM-DD` dates (null = open).
 */
export interface CreditCardTransactionsQuery {
  bankAccountId: string
  from: string | null
  to: string | null
  /** 1-based, as shown in the pager. */
  page: number
  pageSize: number
}

/** A page of transactions plus the figures of the WHOLE range, every page included. */
export interface CreditCardTransactionsPage {
  rows: CreditCardTransaction[]
  /** Transactions in the range across all pages. */
  totalCount: number
  /** Signed sum of every transaction in the range, computed by the server. */
  totalAmount: number
}

/**
 * Data access of the credit card "View transactions" dialog, through
 * `get_credit_card_transactions` (ADR 0002: the range total is computation
 * over every page, which a client holding one page cannot do).
 *
 * NOT `bankTransactions.list`: that one drops credit cards by design (ADR 0017).
 *
 * @param rpcClient - resolves the public-schema client lazily, as
 *   `creditCards.list` does, so building `tenantDb` never touches it
 */
export function creditCardTransactionsDb(rpcClient: () => SupabaseClient<Database>, orgId: string) {
  return {
    /**
     * One page of the card's transactions in the range, newest first.
     *
     * The rows need not add up to the card's `bank_accounts.balance`: history
     * starts at the import floor, and a pending row that later posted under a
     * new id may still be present as a stale duplicate (ADR 0013).
     */
    async list(
      query: CreditCardTransactionsQuery,
      signal: AbortSignal,
    ): Promise<DbResult<CreditCardTransactionsPage>> {
      const invalid = pageQueryError(query.page, query.pageSize)
      if (invalid) return { data: null, error: invalid }

      const callRpc = (limit: number, offset: number) =>
        rpcClient()
          .rpc('get_credit_card_transactions', {
            p_org_id: orgId,
            p_bank_account_id: query.bankAccountId,
            // `undefined` → the RPC's DEFAULT NULL → an open bound.
            p_from: query.from ?? undefined,
            p_to: query.to ?? undefined,
            p_limit: limit,
            p_offset: offset,
          })
          .abortSignal(signal)

      const { data, error } = await callRpc(query.pageSize, (query.page - 1) * query.pageSize)
      if (error) return { data: null, error: { message: error.message, code: error.code } }

      const raw = data ?? []
      // The totals repeat on every row: lifted out once and kept off the rows.
      // `Number` because a numeric column crosses PostgREST as JSON and may
      // arrive as a string.
      const rows: CreditCardTransaction[] = raw.map((row) => ({
        id: row.id,
        transaction_date: row.transaction_date,
        amount: Number(row.amount),
        description: row.description,
        vendor: row.vendor,
        pending: row.pending,
      }))
      const first = await totalsRowOf(query.page, raw, () => callRpc(1, 0))
      if (first.error) return { data: null, error: first.error }
      return {
        data: {
          rows,
          totalCount: first.data ? Number(first.data.total_count) : 0,
          totalAmount: first.data ? Number(first.data.total_amount) : 0,
        },
        error: null,
      }
    },
  }
}
